import { Autocomplete, Box, Button, IconButton, Select, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import Header from '../../components/Header';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';

const AdminUserDetail = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);

  const [details, setDetails] = useState({
    username: '',
    password: '',
    countryId: [],
    role: 2,
    guideIds: [],
  });

  const [countryNames, setCountryNames] = useState([]);
  const [guideOptions, setGuideOptions] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);

  // Roles created on the Roles & Permissions screen (Admin and Coordinator are fixed options)
  const getCustomRoles = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles`, { headers: { token } });
      const data = await res.json();
      setCustomRoles(data.success ? data.data.filter((r) => !r.isSystem) : []);
    } catch (e) {
      console.error('Error fetching roles', e);
      setCustomRoles([]);
    }
  };

  const isCustomRoleUser = ![2, 3].includes(Number(details.role));
  const selectedCustomRole = customRoles.find((r) => r.id === Number(details.role));
  const needsDestinations = Number(details.role) === 2 || selectedCustomRole?.dataScope === 'assigned';

  const getCountryNames = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    setCountryNames(data);
  };

  const getAllGuides = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/all-names`, { headers: { token } });
      const data = await res.json();
      setGuideOptions(Array.isArray(data?.guides) ? data.guides : []);
    } catch (e) {
      console.error('Error fetching guides for coordinator assignment', e);
      setGuideOptions([]);
    }
  };

  const getData = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/adminusers/${id}`, { headers: { token } });
    const data = await res.json();
    if (data.success) {
      setDetails({
        username: data.data.username,
        countryId: data.data.countryId,
        role: data.data.role,
        guideIds: Array.isArray(data.data.guideIds) ? data.data.guideIds : [],
        password: '',
      });
    }
  };

  useEffect(() => {
    getCountryNames();
    getAllGuides();
    getCustomRoles();
    getData();
  }, []);

  const handleChange = (e) => {
    setDetails({ ...details, [e.target.name]: e.target.value });
  };

  const handlevalidation = () => {
    const { countryId } = details;
    if (needsDestinations && countryId.length === 0) {
      setOpenToast(true);
      setToastmessage('Please choose Destination');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (handlevalidation()) {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/adminusers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-type': 'application/json',
          token,
        },
        body: JSON.stringify(details),
      });
      const data = await res.json();
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/adminusers');
          }, 2000);
        }
      }
    }
    setLoading(false);
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="ADMIN DETAILS" subtitle="View Admin Details" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form onSubmit={handleFormSubmit}>
        <Box
          style={{
            display: 'grid',
            gap: '1.25rem',
            paddingBottom: '20px',
          }}
        >
          <TextField
            type="email"
            fullWidth
            variant="filled"
            label="Email"
            onChange={handleChange}
            value={details.username}
            name="username"
            disabled
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#fff',
              },
            }}
          />

          <TextField
            type="password"
            fullWidth
            variant="filled"
            label="New Password"
            onChange={handleChange}
            value={details.password}
            name="password"
          />

          <Box>
            <Typography variant="body2" mb={0.5}>
              Role
            </Typography>
            <Select
              fullWidth
              size="small"
              value={details.role}
              // Users with a custom role can be moved to another custom role; Admin / Coordinator stay fixed
              disabled={!isCustomRoleUser}
              onChange={(e) => setDetails({ ...details, role: Number(e.target.value) })}
              sx={{ backgroundColor: colors.primary[500] }}
            >
              {!isCustomRoleUser && <MenuItem value={2}>Admin</MenuItem>}
              {!isCustomRoleUser && <MenuItem value={3}>Coordinator</MenuItem>}
              {isCustomRoleUser &&
                customRoles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
            </Select>
          </Box>

          {/* Country (admins and custom roles limited to assigned destinations) */}
          {needsDestinations && (
            <Autocomplete
              multiple
              options={countryNames || []}
              getOptionLabel={(option) => option.CountryName}
              value={countryNames.filter((country) => details.countryId.includes(country.CountryID))}
              onChange={(event, newValue) => {
                setDetails({ ...details, countryId: newValue.map((item) => item.CountryID) });
              }}
              renderInput={(params) => <TextField {...params} variant="filled" label="Destination" />}
              required
            />
          )}

          {/* Coordinator guide assignment */}
          {Number(details.role) === 3 && (
            <Autocomplete
              multiple
              options={guideOptions || []}
              getOptionLabel={(option) =>
                `${option.name || ''} ${option.surname || ''}`.trim() || option.email || String(option.id)
              }
              value={(guideOptions || []).filter((g) => details.guideIds.includes(g.id))}
              onChange={(event, newValue) => {
                setDetails({ ...details, guideIds: (newValue || []).map((g) => g.id) });
              }}
              renderInput={(params) => <TextField {...params} variant="filled" label="Assigned Guides" />}
            />
          )}

          <Button
            type="submit"
            disabled={loading}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              width: 'fit-content',
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 30px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AdminUserDetail;
